import { Controller } from "@hotwired/stimulus"

// Postcodes nested landshluti > staður > number. Ticking either level ticks
// everything under it, and a level shows as indeterminate when only some of
// what it contains is selected. Adapted from the Rósetta store picker.
export default class extends Controller {
  static targets = ["counter", "totalCounter", "search", "group", "item"]
  static values = { fieldName: String }

  get selector() {
    return `input[name="${this.fieldNameValue}"][type="checkbox"]`
  }

  connect() {
    this.syncAllGroups()
    this.updateCounters()
  }

  toggleGroup(event) {
    const details = event.target.closest("details")
    if (!details) return

    details.querySelectorAll(this.selector).forEach((checkbox) => {
      // A postcode another group already holds is disabled, and ticking the
      // region it sits in must not quietly take it.
      if (checkbox.disabled) return
      checkbox.checked = event.target.checked
    })

    // The levels in between are re-derived from their own postcodes rather than
    // assumed to follow: a staður whose numbers all belong to another group
    // must not end up ticked just because its landshluti was.
    details.querySelectorAll("details").forEach((nested) => this.syncGroup(nested))
    this.syncGroup(details)
    this.syncAncestorGroups(details)
    this.updateCounters()
  }

  syncGroupState(event) {
    const details = event.target.closest("details")
    if (details) {
      this.syncGroup(details)
      this.syncAncestorGroups(details)
    }
    this.updateCounters()
  }

  // Both directions follow the same rule: what stays ticked is only ever what
  // the admin has seen. So selecting takes what the search has left on screen -
  // "velja allt" filtered to Akureyri must not select the whole country - while
  // clearing takes everything, or postcodes the filter is hiding stay ticked
  // and get saved by someone who believes they cleared the list.
  selectAll(event) {
    event.preventDefault()
    this.setAll(true, { visibleOnly: true })
  }

  clearAll(event) {
    event.preventDefault()
    this.setAll(false, { visibleOnly: false })
  }

  setAll(checked, { visibleOnly }) {
    this.element.querySelectorAll(this.selector).forEach((checkbox) => {
      if (checkbox.disabled) return
      if (visibleOnly && checkbox.closest(".d-none")) return
      checkbox.checked = checked
    })
    this.syncAllGroups()
    this.updateCounters()
  }

  // Each postcode carries its landshluti and staður in its search text, so
  // searching "höfuð" keeps every number in the region and "kópa" keeps
  // Kópavogur's. A level is shown when anything inside it survived, and
  // reverts to its starting state when the search is cleared.
  filter() {
    const query = this.hasSearchTarget ? this.searchTarget.value.toLowerCase().trim() : ""

    this.itemTargets.forEach((item) => {
      item.classList.toggle("d-none", query !== "" && !item.dataset.searchText.includes(query))
    })

    this.groupTargets.forEach((group) => {
      const visible = group.querySelectorAll(
        '[data-postcode-picker-target="item"]:not(.d-none)'
      ).length

      group.classList.toggle("d-none", visible === 0)
      group.open = query ? visible > 0 : group.dataset.defaultOpen === "true"
    })
  }

  syncAllGroups() {
    this.element.querySelectorAll("details").forEach((details) => this.syncGroup(details))
  }

  // Disabled postcodes are left out of the sum entirely, so a level whose every
  // free postcode is ticked reads as fully selected even when another group
  // holds some of the rest.
  syncGroup(details) {
    const groupCheckbox = details.querySelector(':scope > summary input[type="checkbox"]')
    if (!groupCheckbox) return

    const available = [...details.querySelectorAll(this.selector)].filter((box) => !box.disabled)
    const selected = available.filter((box) => box.checked).length

    groupCheckbox.checked = selected === available.length && available.length > 0
    groupCheckbox.indeterminate = selected > 0 && selected < available.length
  }

  syncAncestorGroups(details) {
    let current = details.parentElement?.closest("details")
    while (current) {
      this.syncGroup(current)
      current = current.parentElement?.closest("details")
    }
  }

  updateCounters() {
    this.counterTargets.forEach((badge) => {
      const details = badge.closest("details")
      if (!details) return

      const selected = details.querySelectorAll(`${this.selector}:checked`).length
      badge.textContent = `${selected} / ${badge.dataset.total}`
      // Subtle rather than solid: with 88 places on screen a filled badge on
      // each one is a wall of colour.
      badge.classList.toggle("bg-primary-subtle", selected > 0)
      badge.classList.toggle("text-primary-emphasis", selected > 0)
      badge.classList.toggle("text-bg-secondary", selected === 0)
    })

    if (this.hasTotalCounterTarget) {
      // Postcodes another group holds are left out: the counter measures what
      // this group can take, so reaching the total is actually possible.
      const all = [...this.element.querySelectorAll(this.selector)].filter((box) => !box.disabled)
      const checked = all.filter((box) => box.checked).length
      this.totalCounterTarget.textContent = `${checked} / ${all.length}`
    }
  }
}
